import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import type {
  AssetState,
  EngagementStage,
  FlagCategory,
  FlagKind,
  GuideBlockLabel,
  GuideGroup,
  ProblemSource,
  ProposalTag,
  RecommendationTarget,
  RunStatus,
  ScoreGroup,
} from "@/lib/enums";
import { guideKeys, reportingKeys, runKeys, solutionKeys } from "@/lib/query";

/**
 * Read hooks for the Plan Audit screens.
 *
 * Everything here is read-only, which is why there are no mutations and no
 * `invalidateQueries` — the Guide editor holds its edits in local state and is
 * explicit in the UI that publishing is not wired up yet.
 *
 * Dates cross the wire as ISO strings. The server types them as `Date`; these
 * interfaces say `string` because that is what `JSON.parse` actually hands back,
 * and pretending otherwise is how `.toISOString()` blew up in the reporting
 * aggregate. Format at the point of render with the helpers in
 * `@/lib/format-plan-audit`.
 */

// ── Wire types ───────────────────────────────────────────────────────────────

interface PaginatedResponse<Row> {
  data: Row[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface SolutionRow {
  id: string;
  name: string;
  slug: string;
  sector: string;
  stage: EngagementStage;
  versionCount: number;
  firstScore: number | null;
  latestScore: number | null;
  movement: number | null;
  /** The latest scored run measured against a superseded Guide version. */
  reauditDue: boolean;
  latestRun: {
    reference: string;
    status: RunStatus;
    startedAt: string | null;
    durationSeconds: number | null;
    failureReason: string;
  } | null;
}

export interface SolutionVersionRun {
  id: string;
  reference: string;
  status: RunStatus;
  overallScore: number | null;
  guideLabel: string;
  startedAt: string | null;
  durationSeconds: number | null;
  failureReason: string;
  firstPass: boolean;
  flagCount: number;
  hallucinationCount: number;
  gapCount: number;
  hasSectionDetail: boolean;
}

export interface SolutionVersion {
  id: string;
  versionNumber: number;
  label: string;
  tag: string;
  source: string;
  intakeSummary: string;
  createdAt: string;
  runs: SolutionVersionRun[];
}

export interface GroupMovement {
  group: ScoreGroup;
  current: number | null;
  previous: number | null;
  delta: number | null;
}

export interface SolutionDetail {
  id: string;
  name: string;
  slug: string;
  sector: string;
  stage: EngagementStage;
  versions: SolutionVersion[];
  movement: GroupMovement[];
  movementCrossesGuideVersions: boolean;
}

export interface SectionProblem {
  title: string;
  source: ProblemSource;
  body: string;
  quote: string;
  note: string;
}

export interface SectionGuideNote {
  label: GuideBlockLabel;
  body: string;
}

export interface OpenQuestion {
  lead: string;
  question: string;
  owner: string;
}

export interface SectionProposal {
  tag: ProposalTag;
  body: string;
  openQuestions: OpenQuestion[];
}

export interface RunSection {
  id: string;
  name: string;
  group: ScoreGroup;
  score: number;
  fieldsPresent: number;
  fieldsRequired: number;
  absent: boolean;
  verdict: string;
  strengths: string;
  problems: SectionProblem[];
  guideNotes: SectionGuideNote[];
  proposal: SectionProposal | null;
  hasDetail: boolean;
}

export interface RunFlag {
  id: string;
  reference: string;
  kind: FlagKind;
  category: FlagCategory;
  sectionName: string;
  title: string;
  why: string;
  planReference: string;
  planText: string;
  intakeReference: string;
  intakeText: string;
  effect: string;
  intakeTraced: boolean;
}

export interface RunGroupScore {
  group: ScoreGroup;
  mean: number | null;
  sectionCount: number;
  belowBand: number;
}

export interface RunStage {
  title: string;
  detail: string;
  reference: string;
}

export interface RunAsset {
  name: string;
  state: AssetState;
  count: number | null;
}

export interface RunDetail {
  id: string;
  reference: string;
  status: RunStatus;
  overallScore: number | null;
  firstPass: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  failureReason: string;
  executedByEmail: string;
  stages: RunStage[];
  assets: RunAsset[];
  guide: { id: string; label: string };
  planVersion: { id: string; label: string; versionNumber: number; intakeSummary: string };
  solution: { id: string; name: string; slug: string };
  sections: RunSection[];
  groups: RunGroupScore[];
  flags: RunFlag[];
  sectionDetailMissing: boolean;
}

export interface RunListRow {
  id: string;
  reference: string;
  status: RunStatus;
  overallScore: number | null;
  startedAt: string | null;
  durationSeconds: number | null;
  failureReason: string;
  executedByEmail: string;
  guideLabel: string;
  planVersionLabel: string;
  solutionName: string;
  solutionSlug: string;
}

export interface GuideBlock {
  id: string;
  label: GuideBlockLabel;
  body: string;
  required: boolean;
  position: number;
  emptyAndRequired: boolean;
}

export interface GuideSection {
  id: string;
  name: string;
  group: GuideGroup;
  weight: number;
  position: number;
  blocks: GuideBlock[];
  blockingReason: string | null;
}

export interface GuideVersionSummary {
  id: string;
  label: string;
  note: string;
  publishedAt: string | null;
  runCount: number;
}

export interface GuideDetail {
  id: string;
  label: string;
  note: string;
  publishedAt: string | null;
  sections: GuideSection[];
  history: GuideVersionSummary[];
  blockingSections: string[];
}

export interface ReportingData {
  kpis: {
    medianFirstPassScore: number | null;
    medianDelta90Days: number | null;
    plansAudited: number;
    solutionsCovered: number;
    flagsPerPlan: number | null;
    flagsPerPlanDelta90Days: number | null;
    sectionsBelowBandPct: number | null;
    sectionsBelowBandPctPrior: number | null;
  };
  trend: { month: string; meanScore: number; runCount: number }[];
  weakSections: { name: string; weakCount: number; totalCount: number; meanScore: number }[];
  groupAverages: { group: ScoreGroup; mean: number | null; sectionCount: number }[];
  flagMix: { category: FlagCategory; label: string; count: number; percentage: number }[];
  recommendations: { id: string; target: RecommendationTarget; body: string }[];
  sparse: boolean;
}

// ── Fetch ────────────────────────────────────────────────────────────────────

/**
 * One place that turns a non-OK response into an Error, so every screen's
 * `isError` branch has a message worth showing. A 404 is distinguished from a
 * transport failure because "this run does not exist" and "we could not reach
 * the server" need different UI — the former is a dead end, the latter a retry.
 */
async function getJson<T>(url: string, what: string): Promise<T> {
  const res = await fetch(url);
  if (res.status === 404) throw new Error(`${what} not found`);
  if (!res.ok) throw new Error(`Could not load ${what.toLowerCase()}`);
  return res.json() as Promise<T>;
}

/**
 * A write, surfacing the server's own message.
 *
 * The reads above flatten every failure to "could not load X", which is right
 * for a page that can be retried. A write cannot: "Those files have already
 * been filed" and "The Plan Guide has no published version" each tell the user
 * exactly what to do next, and replacing them with a generic string would throw
 * that away.
 */
async function postJson<T>(url: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const message = await res
      .json()
      .then((json: { error?: string }) => json.error)
      .catch(() => null);
    throw new Error(message ?? fallback);
  }
  return res.json() as Promise<T>;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useSolutions(query: DataGridQuery, pageRequest: PageRequest, stage?: string) {
  const params = buildListParams(query, pageRequest);
  if (stage) params.set("stage", stage);
  return useQuery({
    queryKey: [...solutionKeys.list({ ...query, stage }), pageRequest] as const,
    queryFn: () => getJson<PaginatedResponse<SolutionRow>>(`/api/solutions?${params}`, "Solutions"),
  });
}

export function useSolution(slug: string) {
  return useQuery({
    queryKey: solutionKeys.detail(slug),
    queryFn: () => getJson<SolutionDetail>(`/api/solutions/${slug}`, "Solution"),
    enabled: slug.length > 0,
  });
}

export interface FlagFilters {
  search?: string;
  kind?: string;
  category?: string;
}

/**
 * A run, read whole. Flag filters are part of the key rather than applied on the
 * client so the Flags screen's filtering matches the server's, and so a filtered
 * view is separately cached.
 *
 * `refetchWhileRunning` polls a run that is still executing. The prototype's run
 * screen animates a fake progress bar; a real run has no progress signal beyond
 * its status, so the honest equivalent is to re-read and show the stage list.
 */
export function useRun(
  reference: string,
  flags?: FlagFilters,
  options?: { refetchWhileRunning?: boolean },
) {
  const params = new URLSearchParams();
  if (flags?.search) params.set("flagSearch", flags.search);
  if (flags?.kind) params.set("flagKind", flags.kind);
  if (flags?.category) params.set("flagCategory", flags.category);
  const qs = params.size > 0 ? `?${params}` : "";

  return useQuery({
    queryKey: runKeys.detail(reference, flags as Record<string, string | undefined> | undefined),
    queryFn: () => getJson<RunDetail>(`/api/runs/${reference}${qs}`, "Run"),
    enabled: reference.length > 0,
    refetchInterval: (query) => {
      if (options?.refetchWhileRunning !== true) return false;
      // Running only. A queued run is waiting for an audit engine that does not
      // exist in this build, so polling it every 3s forever spends requests on
      // nothing AND implies something is about to change. The queued state on
      // the run screen says plainly that nothing is processing it, and offers a
      // Refresh button for when that stops being true.
      return query.state.data?.status === "running" ? 3_000 : false;
    },
  });
}

export function useRuns(query: DataGridQuery, pageRequest: PageRequest, solutionId?: string) {
  const params = buildListParams(query, pageRequest);
  if (solutionId) params.set("solutionId", solutionId);
  return useQuery({
    queryKey: [...runKeys.list({ ...query, solutionId }), pageRequest] as const,
    queryFn: () => getJson<PaginatedResponse<RunListRow>>(`/api/runs?${params}`, "Runs"),
  });
}

export function useGuide(version?: string) {
  return useQuery({
    queryKey: guideKeys.version(version),
    queryFn: () =>
      getJson<GuideDetail>(
        `/api/guide${version ? `?version=${encodeURIComponent(version)}` : ""}`,
        "Plan Guide",
      ),
  });
}

export function useReporting() {
  return useQuery({
    queryKey: reportingKeys.all,
    queryFn: () => getJson<ReportingData>("/api/reporting", "Reporting"),
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

export interface StartAuditInput {
  solution:
    | { kind: "existing"; slug: string }
    | { kind: "new"; name: string; sector: string; stage: EngagementStage };
  planFileId: string;
  intakeFileIds: string[];
  /** File id → the name the user picked it by, for quoting in a flag later. */
  filenames: Record<string, string>;
}

export interface StartAuditResult {
  reference: string;
  solutionId: string;
  planVersionId: string;
  solutionSlug: string;
  solutionName: string;
  planVersionLabel: string;
  versionNumber: number;
  solutionCreated: boolean;
}

/**
 * Files an already-uploaded plan as a new version and queues an audit of it.
 *
 * Invalidates BOTH run and solution keys: the solutions list carries each row's
 * `latestRun` and `versionCount`, so leaving it alone would show the list
 * unchanged next to a run that plainly exists.
 *
 * No toast here. The caller navigates to the run screen on success, which says
 * more than a toast could, and shows the failure inline beside the button that
 * caused it rather than in a corner the user has already looked away from.
 */
export function useStartAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: StartAuditInput): Promise<StartAuditResult> => {
      const json = await postJson<{ data: StartAuditResult }>(
        "/api/runs",
        input,
        "Could not queue the audit.",
      );
      return json.data;
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: runKeys.all });
      void queryClient.invalidateQueries({ queryKey: solutionKeys.all });
      void queryClient.invalidateQueries({ queryKey: solutionKeys.detail(result.solutionSlug) });
    },
  });
}
